import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { List } from 'immutable';

import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';

import IconSearch from '../../../../images/icons/misc/search.svg';
import IconClose from '../../../../images/header/icon-close.svg';

import NoResults from './NoResults';
import SearchItem from '../SearchItem';

import styles from './Search.module.scss';
import { PRODUCT_LINKS } from '../../../constants';

const Search = ({
  history: { push: navigate },
  onNavigate,
  searchRequest,
  searchCleared,
  onInputFocusChange,
  results,
  loading,
  product
}) => {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef();

  const { value: queryValue } = inputRef.current || {};

  const {
    longName: productName
  } = PRODUCT_LINKS.find((productAvailable) => productAvailable.id === product) || {};

  const computedResults = useMemo(() => {
    if (results) {
      return results.filter(({ type }) => type === 'event');
    }
    return null;
  }, [results]);

  const dispatchQuery = debounce(() => {
    searchRequest(inputRef.current.value);
  }, 300);

  const onSearchClick = () => {
    inputRef.current.focus();
  };

  const clearInput = () => {
    inputRef.current.value = '';
    dispatchQuery.cancel();
    searchCleared();
  };

  const onInputChange = (e) => {
    if (e.target.value !== '') {
      dispatchQuery();
    } else {
      clearInput();
    }
  };

  const dismissSearch = () => {
    setVisible(false);
    clearInput();
  };

  const onInputFocus = () => {
    setVisible(true);
  };

  const handleNavigate = (url) => {
    dismissSearch();

    // if rendered by MobileNavigation
    if (onNavigate) {
      onNavigate({ url });
      return;
    }

    navigate(url);
  };

  const handleKeydown = useCallback((e) => {
    const enterPressed = e.key === 'Enter';

    if (enterPressed && computedResults && computedResults.size) {
      const { id: eventId } = computedResults.first() || {};

      inputRef.current.blur();
      handleNavigate(`/events/${eventId}`);
    }
  }, [computedResults]);

  const renderResults = useMemo(() => {
    if (computedResults && computedResults.size > 0) {
      return computedResults.map((result) => (
        <SearchItem
          key={result.id}
          result={result}
          onNavigate={handleNavigate}
          query={queryValue}
        />
      ));
    }
    return <NoResults product={product} />;
  }, [computedResults, queryValue]);

  useEffect(() => {
    onInputFocusChange(visible);

    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', handleKeydown);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('keydown', handleKeydown);
      }
    };
  }, [visible, inputRef, handleKeydown]);

  useEffect(() => {
    const searchShortcutListener = event => {
      const isSearchFocused = document.activeElement === inputRef.current;
      const userAlreadyInputing = document.activeElement.tagName === 'INPUT';
      if (event.key === 'F' && !isSearchFocused && !userAlreadyInputing) {
        inputRef.current.focus();
      }

      if (event.key === 'Escape' && isSearchFocused) {
        inputRef.current.blur();
        dismissSearch();
      }
    };

    dismissSearch();
    window.addEventListener('keyup', searchShortcutListener);

    return (
      () => {
        window.removeEventListener('keyup', searchShortcutListener);
      }
    );
  }, []);

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--focused']]: visible
  });

  return (
    <div className={mainClass}>
      {visible && (
        <div
          role="button"
          className={styles.overlay}
          onClick={dismissSearch}
        />
      )}
      <div className={styles.inputContainer}>
        <i
          role="button"
          onClick={onSearchClick}
          data-hook="search-button"
          className=".gtm_cta"
        >
          {loading ? (
            <Spinner theme="light" size="small" />
          ) : (
            <IconSearch />
          )}
        </i>
        <input
          type="text"
          data-hook="search-top"
          className=".gtm_cta"
          name="search-top"
          autoComplete="none"
          ref={inputRef}
          onFocus={onInputFocus}
          onChange={onInputChange}
          placeholder={`Search Matchbook ${productName}`}
        />
        {visible && (
          <i
            role="button"
            className={styles.closeButton}
            onClick={dismissSearch}
          >
            <IconClose />
          </i>
        )}
      </div>
      <div className={styles.results}>
        {
          results ?
            renderResults
            :
            null
        }
      </div>
    </div>
  );
};

Search.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  onNavigate: PropTypes.func,
  onInputFocusChange: PropTypes.func.isRequired,
  searchRequest: PropTypes.func.isRequired,
  searchCleared: PropTypes.func.isRequired,
  results: PropTypes.instanceOf(List),
  loading: PropTypes.bool.isRequired,
  product: PropTypes.string.isRequired
};

export default Search;
